import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    q: "How does BotSweep detect bots?",
    a: "BotSweep uses a multi-signal approach: browser fingerprinting, behavioral analysis (mouse movement, typing cadence, scroll patterns), navigation timing, and IP reputation. Our AI model scores each visitor 0–100 in real-time — no CAPTCHAs needed for legitimate users.",
  },
  {
    q: "Will BotSweep slow down my website?",
    a: "No. The SDK is under 4KB gzipped and loads asynchronously — it never blocks your page rendering. Detection runs in the background and adds less than 12ms of overhead per pageview.",
  },
  {
    q: "What data does BotSweep collect?",
    a: "We collect browser fingerprints, behavioral signals, IP addresses, and page paths. We do NOT collect personal data like names, emails, or form inputs from your visitors. All data is encrypted in transit and at rest.",
  },
  {
    q: "How is BotSweep different from CAPTCHA?",
    a: "CAPTCHAs punish all users with puzzles. BotSweep is invisible to legitimate visitors — only suspicious traffic (score 60+) gets a lightweight proof-of-work challenge. Your real users never see a thing.",
  },
  {
    q: "Can I try BotSweep for free?",
    a: "Yes. Our Free tier includes 5,000 pageviews per month with full bot detection and the real-time dashboard. No credit card required — sign up and start protecting your site in under 60 seconds.",
  },
  {
    q: "How long does setup take?",
    a: "Under 60 seconds. Add your domain, copy one script tag into your site's <head>, and you're protected. No DNS changes, no proxy configuration, no server-side code required.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
};

const FAQ = React.forwardRef<HTMLElement>((_, ref) => {
  return (
    <section id="faq" className="relative py-24 md:py-32 px-6">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-[10px] font-mono text-primary uppercase tracking-[0.3em] mb-4">
            FAQ
          </p>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl text-foreground tracking-tight">
            Common questions.
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border/50 rounded-lg px-5 bg-card/30 hover:border-primary/20 transition-colors"
              >
                <AccordionTrigger className="text-sm font-display font-semibold text-foreground hover:no-underline py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm font-mono text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
});

FAQ.displayName = "FAQ";

export default FAQ;
